import { useState } from 'react';
import { C } from '../../lib/theme';
import { styles } from '../../lib/styles';
import { Dialog } from '../Dialog';
import { ApiProblem } from '../../api/client';
import { submitBugReport } from '../../api/feedback';

// #152 — Report a bug. Opened from the Help page. Collects a title +
// description; the current page and browser UA are auto-attached as context
// (no tokens — the server attaches the authoritative submitter + role from the
// verified token). On success the modal shows the created GitHub issue number
// with a link. Self-contained state (not the global modal system) so the entry
// point stays local to the Help surface.

// Mirror the server-side caps (apps/api/src/routes/feedback.ts) so the form
// blocks over-long input before the round trip; the server enforces them too.
const TITLE_MAX = 200;
const DESCRIPTION_MAX = 8_000;

export function ReportBugModal({ page, onClose }: { page: string; onClose: () => void }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ number: number; htmlUrl: string } | null>(null);

  const canSubmit = title.trim().length > 0 && description.trim().length > 0 && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await submitBugReport({
        title: title.trim(),
        description: description.trim(),
        page,
        userAgent: navigator.userAgent.slice(0, 400),
      });
      setCreated(result);
    } catch (err) {
      if (err instanceof ApiProblem) {
        setError(
          err.status === 503
            ? 'Bug reporting is not configured on this deployment yet. Please report the issue to your administrator directly.'
            : err.detail
              ? `${err.title}: ${err.detail}`
              : err.title,
        );
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (created) {
    return (
      <Dialog title="Bug report submitted" onClose={onClose}>
        <div style={{ fontSize: 13, color: C.text, marginBottom: 16 }}>
          Thanks — your report was filed as issue{' '}
          <a href={created.htmlUrl} target="_blank" rel="noreferrer" style={{ color: C.goldText, fontWeight: 600 }}>
            #{created.number}
          </a>
          . The team will review it.
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button style={{ ...styles.btn, ...styles.btnGold }} onClick={onClose}>Done</button>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog title="Report a bug" onClose={onClose}>
      <div style={{ fontSize: 13, color: C.textMuted, marginBottom: 16 }}>
        Describe what went wrong. Your report opens an issue for the team to review. The page you were on
        and your browser details are attached automatically. Do not include passwords or other secrets.
      </div>

      <div style={{ marginBottom: 12 }}>
        <label htmlFor="bug-title" style={{ fontSize: 11, color: C.textMuted }}>Title</label>
        <input
          id="bug-title"
          style={styles.input}
          value={title}
          maxLength={TITLE_MAX}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Short summary of the problem"
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label htmlFor="bug-description" style={{ fontSize: 11, color: C.textMuted }}>Description</label>
        <textarea
          id="bug-description"
          style={{ ...styles.input, minHeight: 120, resize: 'vertical', fontFamily: 'inherit' }}
          value={description}
          maxLength={DESCRIPTION_MAX}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What did you do, what did you expect, and what happened instead?"
        />
      </div>

      {error && (
        <div role="alert" style={{ fontSize: 12, color: C.danger, marginBottom: 16 }}>{error}</div>
      )}

      <div style={{ display: 'flex', gap: 12 }}>
        <button style={{ ...styles.btn, ...styles.btnGold }} disabled={!canSubmit} onClick={submit}>
          {submitting ? 'Submitting…' : 'Submit report'}
        </button>
        <button style={{ ...styles.btn, ...styles.btnOutline }} disabled={submitting} onClick={onClose}>Cancel</button>
      </div>
    </Dialog>
  );
}
